import { useState } from 'react';

import type { SkillFile } from '../../lib/api-client';

/** A node in the in-memory file tree built from flat path strings. */
export interface TreeNode {
  name: string;
  /** Slash-delimited path from the skill root. Empty string for the synthetic root. */
  path: string;
  type: 'file' | 'dir';
  children?: TreeNode[];
  /** Only set for files when ref is non-null (history time-travel). */
  additions?: number;
  deletions?: number;
  /**
   * Synthetic root folder representing the skill itself. Renders without an
   * underlying path and is non-selectable. We always wrap the actual files
   * under one of these so the surface reads as a tree even when the skill
   * has no subdirectories.
   */
  isSyntheticRoot?: boolean;
}

/**
 * Build a nested tree from a flat list of file paths. Directories are
 * synthesized; they don't appear in the API response. The result always has
 * a single top-level node — a synthetic root folder named after the skill —
 * so the UI reads as a tree even when the skill has no subdirectories.
 */
export function buildTree(files: SkillFile[], skillName: string): TreeNode[] {
  const root: TreeNode = { name: '', path: '', type: 'dir', children: [] };
  for (const file of files) {
    const parts = file.path.split('/');
    let cursor = root;
    for (let i = 0; i < parts.length; i += 1) {
      const name = parts[i] ?? '';
      const isLeaf = i === parts.length - 1;
      const childPath = parts.slice(0, i + 1).join('/');
      cursor.children = cursor.children ?? [];
      let next = cursor.children.find((n) => n.name === name);
      if (!next) {
        next = {
          name,
          path: childPath,
          type: isLeaf ? 'file' : 'dir',
          ...(isLeaf ? {} : { children: [] }),
        };
        if (isLeaf) {
          if (file.additions !== undefined) next.additions = file.additions;
          if (file.deletions !== undefined) next.deletions = file.deletions;
        }
        cursor.children.push(next);
      }
      cursor = next;
    }
  }
  sortTree(root);
  const syntheticRoot: TreeNode = {
    name: skillName,
    path: '',
    type: 'dir',
    isSyntheticRoot: true,
    children: root.children ?? [],
  };
  return [syntheticRoot];
}

function sortTree(node: TreeNode) {
  if (!node.children) return;
  node.children.sort((a, b) => {
    // Directories first, then files, both alphabetically.
    if (a.type !== b.type) return a.type === 'dir' ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
  for (const child of node.children) sortTree(child);
}

interface Props {
  node: TreeNode;
  depth: number;
  selectedPath: string | null;
  onSelectFile: (path: string) => void;
}

export function SkillTreeNode({ node, depth, selectedPath, onSelectFile }: Props) {
  const [open, setOpen] = useState(true);

  if (node.type === 'dir') {
    return (
      <div>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          style={rowStyle(depth, false, node.isSyntheticRoot)}
          aria-expanded={open}
        >
          <Indent depth={depth} />
          <span
            style={{
              width: 12,
              display: 'inline-block',
              color: 'var(--text-mute)',
              flexShrink: 0,
            }}
          >
            {open ? '▾' : '▸'}
          </span>
          <span
            style={{
              marginLeft: 4,
              fontWeight: node.isSyntheticRoot ? 600 : 400,
              color: node.isSyntheticRoot ? 'var(--text)' : 'var(--text)',
            }}
          >
            📁 {node.name}
          </span>
        </button>
        {open && node.children && (
          <div>
            {node.children.map((child) => (
              <SkillTreeNode
                key={child.path}
                node={child}
                depth={depth + 1}
                selectedPath={selectedPath}
                onSelectFile={onSelectFile}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  const isSelected = selectedPath === node.path;
  const hasDelta = node.additions !== undefined || node.deletions !== undefined;
  return (
    <button
      type="button"
      onClick={() => onSelectFile(node.path)}
      style={rowStyle(depth, isSelected, false)}
      aria-current={isSelected ? 'true' : undefined}
    >
      <Indent depth={depth} />
      <span style={{ width: 12, display: 'inline-block', flexShrink: 0 }} />
      <span style={{ marginLeft: 4, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        📄 {node.name}
      </span>
      {hasDelta && (
        <span
          style={{
            fontSize: 10,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            display: 'flex',
            gap: 4,
            flexShrink: 0,
            marginLeft: 6,
          }}
        >
          {(node.additions ?? 0) > 0 && (
            <span style={{ color: 'var(--kind-success, #2da44e)' }}>+{node.additions}</span>
          )}
          {(node.deletions ?? 0) > 0 && (
            <span style={{ color: 'var(--kind-failure, #cf222e)' }}>-{node.deletions}</span>
          )}
        </span>
      )}
    </button>
  );
}

/** Vertical guide lines at each nesting depth — makes the tree shape obvious. */
function Indent({ depth }: { depth: number }) {
  if (depth === 0) return null;
  return (
    <>
      {Array.from({ length: depth }).map((_, i) => (
        <span
          key={i}
          aria-hidden
          style={{
            width: 14,
            display: 'inline-block',
            flexShrink: 0,
            borderLeft: '1px solid var(--border)',
            height: 20,
            marginRight: 0,
          }}
        />
      ))}
    </>
  );
}

function rowStyle(
  depth: number,
  selected: boolean,
  isSyntheticRoot?: boolean,
): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: `4px 8px 4px ${depth === 0 ? 8 : 0}px`,
    border: 0,
    background: selected
      ? 'var(--accent-fade, var(--bg-alt2))'
      : isSyntheticRoot
        ? 'transparent'
        : 'transparent',
    color: 'var(--text)',
    fontSize: 12,
    fontFamily: 'inherit',
    cursor: 'pointer',
    textAlign: 'left',
    whiteSpace: 'nowrap',
  };
}
